import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CatalogPropertySharingService } from 'src/api/generated';
import {
  type PropertySharingSetupResponse,
  type PropertySharingCandidate,
  type PropertySharingPreviewBodyParams,
} from 'src/api/generated';

/** Read website availability for the builder; policy uses the existing settings hook. */
export function usePropertySharingSetup(enabled: boolean) {
  return useQuery({
    queryKey: ['catalog/property-sharing', 'setup'],
    queryFn: (): Promise<PropertySharingSetupResponse> =>
      CatalogPropertySharingService.getSetup(),
    enabled,
  });
}

/** Discover reusable identifier categories without loading all tenant records. */
export function usePropertySharingParameters(enabled: boolean) {
  return useInfiniteQuery({
    queryKey: ['catalog/property-sharing', 'parameters'],
    initialPageParam: firstPage(),
    queryFn: ({ pageParam }) =>
      CatalogPropertySharingService.getParameters(pageParam),
    getNextPageParam: (page) => page.nextPageToken,
    enabled,
  });
}

/** Preview an already validated draft; abort obsolete requests and never reuse stale preview data. */
export function usePropertySharingPreview(
  body: PropertySharingPreviewBodyParams | null
) {
  return useQuery({
    queryKey: ['catalog/property-sharing', 'preview', body],
    enabled: body !== null,
    queryFn: async ({ signal }): Promise<PropertySharingCandidate | null> => {
      if (!body) return null;
      const request = CatalogPropertySharingService.preview(body);
      const cancel = () => request.cancel();
      signal.addEventListener('abort', cancel, { once: true });
      try {
        return await request;
      } finally {
        signal.removeEventListener('abort', cancel);
      }
    },
  });
}

/** Typed empty cursor used by category pagination. */
function firstPage(): string | undefined {
  return undefined;
}
